import { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import {
    get,
    first,
    reduce,
    forEach,
    isEmpty,
    keys,
    uniq,
    flatten,
    values,
    transform,
    difference,
    some,
} from 'lodash';
import banners from '@gin-betting/banners';
import core from '@gin-betting/core';
import responsiveComponents from '@gin-betting/responsive-components';

import type { State } from 'types';
import type { BannerItemsByZoneId, MobileWidgetType } from '../../../types';
import {
    ALL,
    MARKETS_ACTIVE_STATUSES,
    MOBILE_TOP_MATCH_LIST_INDEX_CONFIG,
    MARKET_LIMIT,
} from '../../../constants';
import { usePureSelector } from 'hooks';
import { getAttributesByPeriod, makeGetMissingMatches } from '../../../utils';
import { makeSelectZones } from '../../../selectors';

const { useResponsiveContainer } = responsiveComponents;

const {
    entities: {
        selectors: { selectSportEventIdsFromProps, selectMatches },
    },
    utils: { getBettingOptions },
    hooks: { useDeepMemo },
} = core;

const {
    actions: { getBannersData },
    selectors: { makeSelectBannerZonesByAttributes },
} = banners;

interface Output {
    periods: MobileWidgetType[];
    sportIds: string[];
    missingSportEventIds: string[];
    activeZones: BannerItemsByZoneId | undefined;
    activePeriod?: MobileWidgetType;
    activeSport: string;
    makeHandleActivePeriod: (zoneIndex: MobileWidgetType) => VoidFunction;
    makeHandleChangeActiveSport: (sportId: string) => VoidFunction;
    containerRef: (node: Element | null) => void;
    slideWidth: number;
}

const DEFAULT_SPORT = ALL;

const requestedZoneIdsCache: string[] = [];
const requestedSportEventIdsCache: string[] = [];

const attributesByPeriod = getAttributesByPeriod(
    MOBILE_TOP_MATCH_LIST_INDEX_CONFIG
);

const selectMissingSportEventIds = createSelector(
    selectSportEventIdsFromProps,
    selectMatches,
    (sportEventIds, sportEvents) =>
        sportEventIds.filter(id => {
            const sportEvent = sportEvents.get(id);

            return !sportEvent || sportEvent.markets.length < MARKET_LIMIT;
        })
);

const useMobileTopMatchList = (): Output => {
    const dispatch = useDispatch();
    const { containerRef, currentWidth } = useResponsiveContainer({
        breakpoints: {},
    });

    const [activeSport, setActiveSport] = useState<string>(DEFAULT_SPORT);
    const [activePeriod, setActivePeriod] = useState<
        MobileWidgetType | undefined
    >();

    const makeHandleActivePeriod = useCallback(
        (zoneIndex: MobileWidgetType) => () => {
            setActivePeriod(zoneIndex);
            setActiveSport(DEFAULT_SPORT);
        },
        []
    );

    const makeHandleChangeActiveSport = useCallback(
        (sportId: string) => () => setActiveSport(sportId),
        []
    );

    const bannerZonesByPeriod = usePureSelector((state: State) =>
        createStructuredSelector(bannerZonesSelectorsByPeriod)(state, {})
    );

    const memBannerZonesByPeriod = useDeepMemo(bannerZonesByPeriod);

    const memZoneIdsByPeriod = useMemo(
        () =>
            transform(
                memBannerZonesByPeriod,
                (
                    acc,
                    bannerItems,
                    zoneId: keyof typeof memBannerZonesByPeriod
                ) => {
                    acc[zoneId] = bannerItems.map(({ id }) => id);
                }
            ) as { [key in MobileWidgetType]: string[] },
        [memBannerZonesByPeriod]
    );

    const zonesByPeriod = usePureSelector((state: State) =>
        makeZonesSelectorByPeriods(memZoneIdsByPeriod)(state, {})
    );

    const memZonesByPeriod = useDeepMemo(zonesByPeriod);

    const allZonesFirstSportEventIds = useMemo(
        () =>
            reduce(
                memZonesByPeriod,
                (acc: string[], items) => {
                    forEach(items, item => {
                        const all = get(item, ALL, undefined);

                        if (!all) return;

                        const firstId: string = all.values().next().value;

                        acc.push(firstId);
                    });

                    return acc;
                },
                []
            ),
        [memZonesByPeriod]
    );

    useEffect(() => {
        const newIds = difference(
            allZonesFirstSportEventIds,
            requestedSportEventIdsCache
        );

        if (isEmpty(newIds)) return;

        requestedSportEventIdsCache.push(...newIds);

        dispatch(makeGetMissingMatches(newIds));
    }, [allZonesFirstSportEventIds, dispatch]);

    const periods = useMemo(
        () =>
            reduce(
                memZonesByPeriod,
                (acc, zoneIds, key: MobileWidgetType) => {
                    const isWidgetTypeAvailable = some(
                        zoneIds,
                        matchIdsBySport => !isEmpty(matchIdsBySport)
                    );

                    if (isWidgetTypeAvailable) {
                        acc.push(key);
                    }

                    return acc;
                },
                [] as MobileWidgetType[]
            ),
        [memZonesByPeriod]
    );

    const activeZones = activePeriod
        ? memZonesByPeriod[activePeriod]
        : undefined;

    const { sportEventIds, sportIds } = useMemo(
        () => getUniqSportsAndSportEventIds(activeZones, activeSport),
        [activeSport, activeZones]
    );

    const missingSportEventIds = usePureSelector((state: State) =>
        selectMissingSportEventIds(state, { sportEventIds })
    );

    const memMissingSportEventIds = useDeepMemo(missingSportEventIds);

    useEffect(() => {
        const firstPeriod = first(periods);

        if (!firstPeriod) return;

        setActivePeriod(firstPeriod);
    }, [periods]);

    useEffect(() => {
        flatten(values(memZoneIdsByPeriod))
            .filter(zoneId => !requestedZoneIdsCache.includes(zoneId))
            .forEach(zoneId => {
                requestedZoneIdsCache.push(zoneId);
                dispatch(
                    getBannersData({
                        zoneId,
                        marketStatuses: MARKETS_ACTIVE_STATUSES,
                        withSportEvent: false,
                        sportIds: getBettingOptions().sportIds,
                    })
                );
            });
    }, [dispatch, memZoneIdsByPeriod]);

    return {
        periods,
        sportIds,
        missingSportEventIds: memMissingSportEventIds,
        activeZones,
        activePeriod,
        activeSport,
        makeHandleActivePeriod,
        makeHandleChangeActiveSport,
        slideWidth: currentWidth ?? 0,
        containerRef,
    };
};

const bannerZonesSelectorsByPeriod = transform(
    attributesByPeriod,
    (acc, item, key: MobileWidgetType) => {
        acc[key] = makeSelectBannerZonesByAttributes(item);
    }
) as {
    [key in MobileWidgetType]: ReturnType<
        typeof makeSelectBannerZonesByAttributes
    >;
};

const makeZonesSelectorByPeriods = (
    data: { [key in MobileWidgetType]: string[] }
) => {
    const selectProps = transform(
        data,
        (acc, zoneIds, period: MobileWidgetType) => {
            acc[period] = makeSelectZones(zoneIds);
        }
    ) as { [key in MobileWidgetType]: ReturnType<typeof makeSelectZones> };

    return createStructuredSelector(selectProps);
};

const getUniqSportsAndSportEventIds = (
    activeZones: BannerItemsByZoneId | undefined,
    activeSport: string
): {
    sportEventIds: string[];
    sportIds: string[];
} => {
    const sportEventIds: string[] = [];
    const sportIds: string[] = [];

    if (!activeZones) {
        return {
            sportEventIds,
            sportIds,
        };
    }

    forEach(activeZones, matchIdsBySport => {
        if (!isEmpty(matchIdsBySport)) {
            const nextSportEventIds = get(
                matchIdsBySport,
                activeSport,
                undefined
            );

            if (nextSportEventIds) {
                sportEventIds.push(...Array.from(nextSportEventIds));
            }

            sportIds.push(...keys(matchIdsBySport));
        }
    });

    return {
        sportEventIds: uniq(sportEventIds),
        sportIds: uniq(sportIds),
    };
};

export default useMobileTopMatchList;
